import { useCloseDeletedSelection } from "../../components/common/useCloseDeletedSelection";
import { useEffect, useMemo, useRef, useState } from "react";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import Loading from "../../components/common/Loading";
import { RedirectService } from "../../../services/RedirectService";
import { IJSTreeNode, ILanguage, IPage, IRedirect } from "../../../models/Interfaces";
import { ContentService } from "../../../services/ContentService";
import { useConfirm } from "../../../config/ConfirmProvider";
import { CellProps, Column } from "react-table";
import { Button, Col, Form, FormControl, FormLabel, Row } from "react-bootstrap";
import { faCircleInfo, faEdit, faMagnifyingGlass, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FilteredTable from "../../components/common/FilteredTable";
import AppModal from "../../components/common/AppModal";
import PageSelector from "../../components/common/PageSelector";
import { Core } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import AppEmptyState from "../../components/common/AppEmptyState";
import styles from "./manageRedirects.module.scss";

const ManageRedirects = () => {

  const [loading, setLoading] = useState<boolean>(true);
  const [redirects, setRedirects] = useState<Array<IRedirect>>([]);
  const [languages, setLanguages] = useState<Array<ILanguage>>([]);
  const [ pages, setPages ] = useState<Array<IPage>>([]);
  const [selectedRedirect, setSelectedRedirect] = useState<IRedirect | null>(null);
  const [showDetail, setShowDetail] = useState<boolean>(false);

  const rdServiceRef = useRef<RedirectService | null>(null);
  const contentServiceRef = useRef<ContentService | null>(null);
  const rdFormRef = useRef<HTMLFormElement | null>(null);
  const rdFvRef = useRef<Core | null>(null);

  const confirmation = useConfirm();

  const columns: Column<IRedirect>[] = useMemo(() => [
    {
      Header: "Site Language",
      id: "language",
      Cell: ({ row }: CellProps<IRedirect>) => {
        return <span>{getLanguageName(row.original.Page?.Language ?? "en-gb")}</span>
      }
    },
    {
      Header: "Source Url",
      accessor: "RedirectUrl"
    },
    {
      Header: "Target Path",
      id: "targetpage",
      Cell: ({ row }: CellProps<IRedirect>) => {
        return <span>{row.original.Page?.Url}</span>
      }
    },
    {
      Header: "Target Page",
      id: "targetpagetitle",
      Cell: ({ row }: CellProps<IRedirect>) => {
        return <span>{getPageTitle(row.original.PageId)}</span>
      }
    },
    {
      id: "Actions",
      Header: "",
      Cell: ({ row }: CellProps<IRedirect>) => (
        <div className="user-result-actions">
          <a
            href={row.original.Page?.Url ?? "#"}
            title="view target page"
            target="_blank"
            className="btn btn-light user-result-actions__btn"
            rel="noreferrer">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </a>
          <Button variant="light" onClick={() => editRedirect(row.original)} className="user-result-actions__btn user-result-actions__btn--view" title="Edit redirect">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button variant="light" onClick={() => deleteRedirect(row.original)} className="user-result-actions__btn user-result-actions__btn--danger" title="Delete redirect">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )
    }
  ], [pages]);

  const getRedirects = async (): Promise<void> => {
    if (rdServiceRef.current) {
      const res = await rdServiceRef.current.getRedirects();
      if (res.length > 0) {
        setRedirects(res);
      }
    }
  };

  const getLanguages = async (): Promise<void> => {
    if (contentServiceRef.current) {
      const res = await contentServiceRef.current.getSiteLanguages();
      if (res.length > 0) {
        setLanguages(res);
      }
    }
  };

  const getPageTitle = (pageid: number) : string => {
    if (pages.length > 0 && pageid) {
      const page = pages.find(p => p.Id === pageid);
      if (page) {
        return page.Title;
      }
    }
    return "";
  }

  const getPages = async (): Promise<void> => {
    if (contentServiceRef.current) {
      const res = await contentServiceRef.current.getAllPages();
      if (res.length > 0) {
        setPages(res);
      }
    }
  };

  const getLanguageName = (shortcode: string): string => {
    if (languages) {
      const lang = languages.find(l => l.ShortCode.toLowerCase() === shortcode.toLowerCase());
      return lang?.Language ?? shortcode;
    }
    return shortcode;
  };

  const addRedirect = (): void => {
    if (rdServiceRef.current) {
      const rd = rdServiceRef.current.newRedirect();
      if (rd) {
        setSelectedRedirect(rd);
        setShowDetail(true);
      }
    }
  };

  const editRedirect = (redirect: IRedirect): void => {
    if (redirect) {
      setSelectedRedirect(redirect);
      setShowDetail(true);
    }
  };

  const saveRedirect = async () : Promise<void> => {
    if (selectedRedirect && rdServiceRef.current && rdFvRef.current) {
      const validres = await rdFvRef.current.validate();

      if (validres === "Valid") {
        const res = await rdServiceRef.current.saveRedirect(selectedRedirect);
        if (res) {
          const existing = redirects.find(r => r.Id === res.Id);
          if (existing) {
            setRedirects(prev => prev.map(r => r.Id === res.Id ? res : r));
          } else {
            setRedirects(prev => [
              ...prev,
              res
            ]);
          }
        }
        closeDetail();
      }
    }
  };

  const handlePropertyChange = (event: React.ChangeEvent<HTMLInputElement>) : void => {
    if (selectedRedirect) {
      let { name, value } = event.target;

      if (name && name === "PageId") {
        value = (value as unknown as IJSTreeNode)?.id?.toString() ?? ""
      }

      if (name) {
        setSelectedRedirect(prev => ({
          ...prev as IRedirect,
          [name]: value
        }));
      }
    }
  };

  const closeDetail = () : void => {
    setShowDetail(false);
    setSelectedRedirect(null);
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedRedirect, (item) => item.Id, () => closeDetail());

  const deleteRedirect = async (redirect: IRedirect): Promise<void> => {
    if (rdServiceRef.current && redirect) {
      const confirmed = await confirmation.requestConfirmation("Delete page redirect? <b>This action cannot be undone.</b>", "Delete Redirect");

      if (confirmed) {
        const res = await rdServiceRef.current.deleteRedirect(redirect.Id);
        if (res) {
          setRedirects(prev => prev.filter(rd => rd.Id !== redirect.Id));
          closeDeletedSelection(redirect.Id);
        }
      }
    }
  };

  const createValidator = () : void => {
    if (rdFormRef.current && !rdFvRef.current) {
      rdFvRef.current = createFormValidation(rdFormRef.current, {
        fields: {
          RedirectUrl: {
            validators: {
              notEmpty: { message: "Url to redirect is required"}
            }
          },
          PageId: {
            validators: {
              notEmpty: { message: "redirect target is required"}
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger()
        }
      });
    }
  };

  useEffect(() => {
    if (rdFormRef.current) {
      createValidator();
    } else {
      rdFvRef.current = null;
    }
  }, [rdFormRef, showDetail]);

  useEffect(() => {
    const getData = async () => {
      try {
        await Promise.all([
          getRedirects(),
          getLanguages(),
          getPages()
        ]);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    if (!rdServiceRef.current) {
      rdServiceRef.current = new RedirectService();
    }

    if (!contentServiceRef.current) {
      contentServiceRef.current = new ContentService();
    }
    getData();
  }, []);

  return (
    <>
      {loading && (
        <Loading Message="Loading redirect configurations..." SpinSize="5x" />
      )}

      {!loading && (
        <div className={styles.page}>
          <section className="app-list-section">
            <div className="app-list-section__header">
              <div>
                <h2 className="app-list-section__title">Redirect Library</h2>
              </div>
            </div>
            <div className={`app-list-section__body ${styles.sectionBody}`}>
              <div id="redirects" className={styles.tableWrap}>
                <FilteredTable<IRedirect>
                  columns={columns}
                  data={redirects}
                  noDataMessage=""
                  showAddButton={true}
                  addButtonText="Add a redirect"
                  addButtonHandler={addRedirect}
                  filterPlaceHolder="filter redirects..."
                  noDataComponent={
                    <AppEmptyState
                      icon={faCircleInfo}
                      compact={true}
                      className={styles.emptyNotice}
                      title="No redirects found"
                      message="No managed redirects have been configured for this site yet."
                    />
                  }
                />
              </div>
            </div>
          </section>
        </div>
      )}

      {showDetail && selectedRedirect && (
        <AppModal
          onClose={closeDetail}
          showButtons={true}
          size="md"
          actionButtonVariant="success"
          cancelButtonVariant="primary"
          cancelButtonText="Cancel"
          dataActionButtonText="Save"
          actionButtonIcon={faSave}
          title="Redirect Details"
          onDataAction={saveRedirect}>
            <Form id="RedirectForm" ref={rdFormRef}>
              <Row>
                <Col md={12}>
                  <FormLabel>URL to redirect (e.g. /redirect/this/page)</FormLabel>
                  <FormControl
                    type="text"
                    value={selectedRedirect.RedirectUrl ?? ""}
                    title="url to redirect"
                    placeholder="url to redirect..."
                    name="RedirectUrl"
                    id="RedirectUrl"
                    onChange={handlePropertyChange}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mt-3">
                  <FormLabel>Redirect Target Page</FormLabel>
                  <PageSelector
                    selectedPageId={selectedRedirect.PageId}
                    onInputSelect={handlePropertyChange}
                    allowInput={false}
                    name="PageId"
                    closeOnSelect={true}
                  />
                </Col>
              </Row>
            </Form>
        </AppModal>
      )}
    </>
  );
}

export default ManageRedirects;
